import React, { useMemo } from 'react';
import { Box, Chip, Typography } from '@saviynt/design-system';

import { shouldShowEntitlementsRequestedRow } from '../../../pages/AuditLogViewer/data-layer/auditLogUtils';
import { useDateFormat } from '../../../pages/AuditLogViewer/hooks';
import {
  formatDateByGlobal,
  formatInTimeZoneWithDisplayFormat,
  getDisplayFormatForGlobalDateFormat,
} from '../../../pages/AuditLogViewer/utils/utils';
import { useInvertedLocalize, useTypedSelector } from '../../hooks';
import { formatDisply, getBrowserTimezone } from '../../utils';

/**
 * RequestDetails
 * Reusable list of request details rendered with the same markup/classes
 * as the header popover body list items.
 */
function RequestDetails(): React.ReactElement {
  const l = useInvertedLocalize();
  const requestMetadata = useTypedSelector(
    (s) => s.aag.auditLog.requestMetadata
  );
  const globalDateFormat = useDateFormat();
  const displayFormat = getDisplayFormatForGlobalDateFormat(globalDateFormat);

  const reqStart = requestMetadata?.requestStartedAt;
  const reqEnd = requestMetadata?.requestEndedAt;
  const reqTZ = requestMetadata?.sourceTimeZone;

  // Get browser's local timezone abbreviation for "Time Requested"
  const browserTZ = useMemo(() => {
    if (reqStart) {
      const date = new Date(reqStart);

      return getBrowserTimezone(date);
    }

    return '';
  }, [reqStart]);

  return (
    <>
      <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
          <Typography kind='body3'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
              {l('User')}
            </span>
          </Typography>
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
          <Typography kind='body2'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
              {formatDisply(requestMetadata?.requestorUserDisplayName) ?? '-'}
            </span>
          </Typography>
        </Box>
      </Box>

      <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
          <Typography kind='body3'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
              {(requestMetadata?.ffRoleName ?? '').trim() !== ''
                ? l('Firefighter Role')
                : l('FFID')}
            </span>
          </Typography>
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
          <Typography kind='body2'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
              {formatDisply(
                (requestMetadata?.ffRoleName ?? '').trim() !== ''
                  ? requestMetadata?.ffRoleName
                  : requestMetadata?.ffid
              ) ?? '-'}
            </span>
          </Typography>
        </Box>
      </Box>

      <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
          <Typography kind='body3'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
              {l('Ticket Number')}
            </span>
          </Typography>
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
          <Typography kind='body2'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
              {formatDisply(requestMetadata?.ticketNumber) ?? '-'}
            </span>
          </Typography>
        </Box>
      </Box>

      <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
          <Typography kind='body3'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
              {l('Justification (Requestor)')}
            </span>
          </Typography>
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
          <Typography kind='body2'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
              {formatDisply(requestMetadata?.businessJustification) ?? '-'}
            </span>
          </Typography>
        </Box>
      </Box>

      <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
          <Typography kind='body3'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
              {l('Time Requested')}
            </span>
          </Typography>
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
          <Typography kind='body2'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
              {reqStart && reqEnd && browserTZ
                ? `${formatDateByGlobal(
                    reqStart,
                    globalDateFormat
                  )} (${browserTZ}) to ${formatDateByGlobal(
                    reqEnd,
                    globalDateFormat
                  )} (${browserTZ})`
                : '-'}
            </span>
          </Typography>
        </Box>
      </Box>

      <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
          <Typography kind='body3'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
              {l('Time Requested in Source System Time')}
            </span>
          </Typography>
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
          <Typography kind='body2'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
              {reqStart && reqEnd && reqTZ
                ? `${formatInTimeZoneWithDisplayFormat(
                    reqStart,
                    reqTZ,
                    displayFormat
                  )} (${reqTZ}) to ${formatInTimeZoneWithDisplayFormat(
                    reqEnd,
                    reqTZ,
                    displayFormat
                  )} (${reqTZ})`
                : '-'}
            </span>
          </Typography>
        </Box>
      </Box>

      <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
          <Typography kind='body3'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
              {l('Request ID')}
            </span>
          </Typography>
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
          <Typography kind='body2'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
              {formatDisply(requestMetadata?.requestId) ?? '-'}
            </span>
          </Typography>
        </Box>
      </Box>

      {shouldShowEntitlementsRequestedRow(requestMetadata?.requestType) ? (
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
          <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
            <Typography kind='body3'>
              <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                {l('Entitlements Requested')}
              </span>
            </Typography>
          </Box>
          <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
            {Array.isArray(requestMetadata?.requestedEntitlementDisplayNames) &&
            requestMetadata.requestedEntitlementDisplayNames.length ? (
              <Box className='Sod-AuditLogViewerHeader-chipList'>
                {requestMetadata.requestedEntitlementDisplayNames.map(
                  (name) => (
                    <Chip key={name} size='xSmall' label={name} />
                  )
                )}
              </Box>
            ) : (
              <Typography kind='body2'>
                <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                  {l('-')}
                </span>
              </Typography>
            )}
          </Box>
        </Box>
      ) : null}

      <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
          <Typography kind='body3'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
              {l('Approver')}
            </span>
          </Typography>
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
          <Typography kind='body2'>
            <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
              {formatDisply(requestMetadata?.approverUserDisplayName) ?? '-'}
            </span>
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default RequestDetails;
